// Phase 3D.1-B4-C - Pure, testable KPI selectors for the Savoie pilot demo.
// Operate ONLY on the records array returned by the /api/demo/savoie endpoint
// (via useSavoieDemo). No hardcoded Savoie figures, no network, no fabrication.
// Any impossible division / missing input yields the explicit MISSING token.

export const MISSING = "Non disponible";

const METRIC = {
  ARRIVALS: "VISITOR_ARRIVALS",
  OVERNIGHT: "OVERNIGHT_STAYS",
  SPEND: "TOURISM_SPEND",
};

const MONTHS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const _num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const _year = (r) => r && r.period && r.period.reference_year;
const _month = (r) => r && r.period && r.period.month;

// Most recent (year, month) present for VISITOR_ARRIVALS (primary metric).
export function latestPeriod(records) {
  if (!Array.isArray(records)) return null;
  let best = null;
  for (const r of records) {
    if (!r || r.metric_code !== METRIC.ARRIVALS) continue;
    const y = _year(r);
    const m = _month(r);
    if (typeof y !== "number" || typeof m !== "number") continue;
    const key = y * 12 + m;
    if (!best || key > best.key) best = { year: y, month: m, key };
  }
  return best ? { year: best.year, month: best.month } : null;
}

export function periodLabel(period) {
  if (!period) return MISSING;
  const name = MONTHS_FR[period.month - 1] || "";
  return `${name} ${period.year}`.trim();
}

// Sum of a metric for a given market at a given period (null when no record exists).
function marketValue(records, code, market, year, month) {
  let found = false;
  let total = 0;
  for (const r of records) {
    if (!r || r.metric_code !== code || r.source_market !== market) continue;
    if (_year(r) !== year || _month(r) !== month) continue;
    const v = _num(r.value);
    if (v === null) continue;
    total += v;
    found = true;
  }
  return found ? total : null;
}

function marketsAt(records, code, year, month) {
  const set = new Set();
  for (const r of records) {
    if (r && r.metric_code === code && _year(r) === year && _month(r) === month
        && _num(r.value) !== null) {
      set.add(r.source_market);
    }
  }
  return Array.from(set);
}

function totalMetric(records, code, year, month) {
  let found = false;
  let total = 0;
  for (const r of records) {
    if (!r || r.metric_code !== code) continue;
    if (_year(r) !== year || _month(r) !== month) continue;
    const v = _num(r.value);
    if (v === null) continue;
    total += v;
    found = true;
  }
  return found ? total : null;
}

// KPI 1 - Visitor arrivals by market (VISITOR_ARRIVALS only), latest period.
export function arrivalsByMarket(records, period) {
  if (!Array.isArray(records) || !period) return [];
  const markets = marketsAt(records, METRIC.ARRIVALS, period.year, period.month);
  return markets
    .map((m) => ({ market: m, value: marketValue(records, METRIC.ARRIVALS, m, period.year, period.month) }))
    .filter((x) => x.value !== null)
    .sort((a, b) => b.value - a.value);
}

// KPI 2 - Arrivals growth vs same month one year earlier (VISITOR_ARRIVALS only).
export function growthByMarket(records, period) {
  if (!Array.isArray(records) || !period) return [];
  const markets = marketsAt(records, METRIC.ARRIVALS, period.year, period.month);
  return markets.map((m) => {
    const cur = marketValue(records, METRIC.ARRIVALS, m, period.year, period.month);
    const prev = marketValue(records, METRIC.ARRIVALS, m, period.year - 1, period.month);
    if (cur === null || prev === null || prev === 0) return { market: m, growthPct: MISSING };
    return { market: m, growthPct: ((cur - prev) / prev) * 100 };
  }).sort((a, b) => {
    const av = a.growthPct === MISSING ? -Infinity : a.growthPct;
    const bv = b.growthPct === MISSING ? -Infinity : b.growthPct;
    return bv - av;
  });
}

// KPI 3 - Market concentration: top market share of total arrivals (latest period).
export function concentration(records, period) {
  const rows = arrivalsByMarket(records, period);
  if (!rows.length) return { market: MISSING, sharePct: MISSING };
  const total = rows.reduce((s, r) => s + r.value, 0);
  if (total <= 0) return { market: MISSING, sharePct: MISSING };
  const top = rows[0];
  return { market: top.market, sharePct: (top.value / total) * 100 };
}

// KPI 4 - Average length of stay = total OVERNIGHT_STAYS / total VISITOR_ARRIVALS (nights).
export function averageStay(records, period) {
  if (!Array.isArray(records) || !period) return { nights: MISSING };
  const nights = totalMetric(records, METRIC.OVERNIGHT, period.year, period.month);
  const arrivals = totalMetric(records, METRIC.ARRIVALS, period.year, period.month);
  if (nights === null || arrivals === null || arrivals === 0) return { nights: MISSING };
  return { nights: nights / arrivals };
}

// KPI 5 - Average tourism spend per visitor.
// B1 semantics (proven in tourism_demo_savoie.py: value = arrivals * AVERAGE_SPEND_EUR):
// TOURISM_SPEND is a TOTAL amount => average = total spend / total visitors.
export function averageSpendPerVisitor(records, period) {
  if (!Array.isArray(records) || !period) return { value: MISSING, currency: null };
  const spend = totalMetric(records, METRIC.SPEND, period.year, period.month);
  const arrivals = totalMetric(records, METRIC.ARRIVALS, period.year, period.month);
  if (spend === null || arrivals === null || arrivals === 0) return { value: MISSING, currency: null };
  let currency = null;
  for (const r of records) {
    if (r && r.metric_code === METRIC.SPEND && r.currency) { currency = r.currency; break; }
  }
  return { value: spend / arrivals, currency };
}

export const _internals = { METRIC, marketValue, totalMetric, marketsAt };
